/**
 * Simple helper functions shared across components.
 */

// Values that may need tweaked.
const GOP_PARTY_VALUE = "rep";
const DEM_PARTY_VALUE = "dem";
const LIB_PARTY_VALUE = "lib";


function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escape special characters
}

export function titleContainsText(filterText, options = [], inputSource) {
  return (race) => {
    // Split the filter text into words and convert to lowercase
    const explodeFilterString = (flatStr) => {
      return flatStr.trim().split(' ').map((s) => s.toLowerCase());
    };

    // Construct searchable fields for the race
    let searchableFields = `${race.name} ${race.name_override} ${race.raceSource} ${race.description}`;
    
    if (!race.isRaceAnIssue) {
      const raceCandidates = race.candidates || [];
      raceCandidates.forEach((c) => {
        searchableFields += ` ${c.name} ${c.name_override}`;
      });
    }

    // Handle countyFilter case (dropdown)
    if (inputSource === 'countyFilter') {
      if (options.length > 0 && filterText && filterText.length) {
        const selectedOption = options.find(option => 
          option.label.toLowerCase() === filterText.toLowerCase()
        );

        if (selectedOption) {
        // Add label to valuesArray for searching
        const valuesArray = [selectedOption.label, ...selectedOption.value];

        if (Array.isArray(valuesArray)) {
          return valuesArray.some(value =>
            searchableFields.toLowerCase().includes(value.toLowerCase())
          );
        }
      }
    }
    return true; // If no filterText or options are selected, include all races
  }

    // Handle generalFilter case (text input)
    if (inputSource === 'generalFilter') {
      if (filterText && filterText.length) {
        const lowerSearchableFields = searchableFields.toLowerCase();
        const lowerFilterText = filterText.toLowerCase();

        // Check for exact or start-word matches
        const matches = explodeFilterString(filterText).every((fStr, index, array) => {
          const isLastWord = index === array.length - 1;
          const nextFStr = array[index + 1] || '';

          if (isLastWord) {
            const escapedFStr = escapeRegExp(fStr);
            const startWordRegex = new RegExp(`\\b${escapedFStr}`, 'i');
            return startWordRegex.test(lowerSearchableFields);
          } else {
            const escapedFStr = escapeRegExp(fStr);
            const escapedNextFStr = escapeRegExp(nextFStr);
            const nextWordRegex = new RegExp(`\\b${escapedFStr}\\b[^\\S\\r\\n]*${escapedNextFStr}`, 'i');
            return nextWordRegex.test(lowerSearchableFields);
          }
        });

        const fullTextMatch = lowerSearchableFields.includes(lowerFilterText);

        return matches || fullTextMatch;
      }
      return true; // If no filterText, include all races
    }

    return true;
  };
}


// this helper is for the precinct progress bar
export function getAffiliationColor(affiliation) {
  switch (affiliation.toLowerCase().trim()) {
    case GOP_PARTY_VALUE:
    case "gop":
    case "r":
    case "rep":
      return "#e23834";
    case DEM_PARTY_VALUE:
    case "dem":
    case "d":
      return "#01236a";
    case LIB_PARTY_VALUE:
    case "lib":
      return "#f7b709";
    case "yes":
      return "#333";
    case "no":
      return "#333";
    default:
      return "#333";
  }
}

/**
 * Gets the override value if present and not null.
 *
 * Override field is expected to be of the format: ${field}_override.
 *
 * @param {object} data
 * @param {string} field
 * @returns {string} Field value or undefined
 */
export function getOverride(data, field) {
  const overrideField = `${field}_override`;
  return data.hasOwnProperty(overrideField) && data[overrideField] !== null
    ? data[overrideField]
    : data[field];
}

/**
 * Percent shown next to the precinct progress bar: AP's estimated expected vote percentage
 * (`eevp`), read straight from the race / county reporting unit. Never calculated from
 * precinct counts and never read from precinctsPercent. There is no eevp override.
 * Returns a whole number 0-100, or null when blank (null/undefined/''/whitespace/non-numeric).
 * Math.floor on purpose: 99.6 must not display as 100% before AP reports 100. The value is
 * clamped to 0-100 first, and the bar width uses this same whole number. A real 0 (EEVP "0.00")
 * returns 0.
 */
export function getEstimatedVotesPercent(data) {
  const value = data ? data.eevp : undefined;
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.floor(Math.min(100, Math.max(0, number)));
}

/**
 * Overall race-level AP EEVP (`race.eevp`, mapped from the statewide ReportingUnit EEVP) as a sentence, or null.
 * Rounds with the processor rule (apelectionresults.py): ceil if the fraction is >= .5, else floor.
 * Accepts a number or a plain numeric string; anything non-finite, negative, over 100 or non-numeric
 * returns null. A real 0 is valid ("0% of the estimated votes counted").
 */
export function getRaceEevpSentence(data) {
  const value = data ? data.eevp : undefined;
  let number;
  if (typeof value === "number") number = value;
  else if (typeof value === "string" && /^\s*\d+(\.\d+)?\s*$/.test(value)) number = Number(value.trim());
  else return null;
  if (!Number.isFinite(number) || number < 0 || number > 100) return null;
  const rounded = number % 1 >= 0.5 ? Math.ceil(number) : Math.floor(number);
  return `${rounded}% of the estimated votes counted`;
}

/**
 * Uses the `raceMetadata` field of the widget config, if present, to sort and
 * filter the given races.
 *
 * Candidate metadata from the widget config will also be merged into the race
 * data. If useRacesForCandidateList is true, then use the race results as the
 * source of truth for candidates list instead of the widget config.
 *
 * If widgetConfig has no `raceMetadata` field, `races` will be returned
 * unchanged.
 *
 * @param {Object} widgetConfig
 * @param {Object[]} races
 * @param {boolean} useRacesForCandidateList
 * @returns {Object[]} Updated list of races
 */
export function sortAndMergeRaces(widgetConfig, races, useRacesForCandidateList) {
  if (!widgetConfig["raceMetadata"]) {
    return races;
  }

  const sortedRaces = widgetConfig["raceMetadata"].map((raceMeta) => {
    const race = races.find(r => (r.id ?? r.raceID) === raceMeta["raceId"]);

    // Merge candidate meta.
    if (race && raceMeta["candidateMetadata"]) {
      const updatedCandidates = sortAndMergeCandidates(
        raceMeta["candidateMetadata"], race.candidates, useRacesForCandidateList
      );
      race.candidates = updatedCandidates;
    }

    return race;
  });

  // Remove cases where a corresponding race wasn't found in `races`.
  const filtered = sortedRaces.filter(r => r !== undefined);
  // If nothing matched (e.g. AP format raceID mismatch), return original races
  return filtered.length > 0 ? filtered : races;
}

/**
 * Applies the per-widget "Race Order" setting (`widget.raceSort`) to the races.
 * Call this AFTER sortAndMergeRaces so it overrides the widget metadata order.
 *
 * - raceSort === "alphabetical": sort A-Z by displayed name (name_override when
 *   set, else name), case-insensitive.
 * - Legacy: the widget whose id matches `legacyAlphabeticalWidgetId`
 *   (REACT_APP_ALPHABETICAL_SORT_WIDGET_ID) stays alphabetical when `raceSort`
 *   is absent or anything other than "alphabetical" (including an explicit
 *   "default"), using the original rule exactly as before: raw `race.name`,
 *   default localeCompare.
 * - Anything else: races are returned untouched (same array).
 *
 * @param {Object} widget Widget config (may be empty/undefined)
 * @param {string} widgetId Id of the widget being rendered
 * @param {Object[]} races
 * @param {string} [legacyAlphabeticalWidgetId]
 * @returns {Object[]} Sorted copy of races, or the original races
 */
export function applyRaceSort(widget, widgetId, races, legacyAlphabeticalWidgetId) {
  if (!Array.isArray(races)) {
    return races;
  }

  if (widget && widget.raceSort === "alphabetical") {
    const displayedName = (race) => (race && getOverride(race, "name")) || "";
    return [...races].sort((a, b) =>
      displayedName(a).localeCompare(displayedName(b), undefined, { sensitivity: "accent" })
    );
  }

  // TODO: Legacy hardcoded widget; remove once it has raceSort=alphabetical set in the admin.
  if (legacyAlphabeticalWidgetId && widgetId === legacyAlphabeticalWidgetId) {
    return [...races].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }

  return races;
}

/**
 * Sort candidates and merge website/image based on candidateMeta.
 *
 * @param {Object[]} candidateMetadata
 * @param {Object[]} candidates
 * @param {boolean} useRacesForCandidateList
 * @returns {Object[]} Updated list of candidates
 */
function sortAndMergeCandidates(candidateMetadata, candidates, useRacesForCandidateList) {
  let sortedCandidates;
  if (useRacesForCandidateList) {
    sortedCandidates = sortAndMergeRaceCandidates(candidateMetadata, candidates);
  } else {
    sortedCandidates = sortAndMergeWidgetConfigCandidates(candidateMetadata, candidates);
  }

  return sortedCandidates;
}

/**
 * Sort candidates using races as source of truth
 * and merge website/image based on candidateMeta.
 * First order by candidateMeta, then append any extra candidates
 * not present in candidateMeta.
 *
 * @param {Object[]} candidateMetadata
 * @param {Object[]} candidates
 * @returns {Object[]} Updated list of candidates
 */
function sortAndMergeRaceCandidates(candidateMetadata, candidates) {
  let sortedAndMerged = [];
  let sortedAndMergedIDs = {};

  // sort and merge website/image, based on candidateMeta
  sortedAndMerged = sortAndMergeWidgetConfigCandidates(candidateMetadata, candidates);

  // then gather IDs of sorted-and-merged list
  sortedAndMerged.forEach(sm => {
    sortedAndMergedIDs[sm.id] = true;
  });
  
  // then gather any extra candidates not in sorted-and-merged list
  candidates.forEach(candidate => {
    if(!sortedAndMergedIDs[candidate.id]) {
      sortedAndMerged.push(candidate);
    }
  });

  return sortedAndMerged;
}

/**
 * Sort candidates using widget config as source of truth
 * and merge website/image based on candidateMeta.
 *
 * @param {Object[]} candidateMetadata
 * @param {Object[]} candidates
 * @returns {Object[]} Updated list of candidates
 */
function sortAndMergeWidgetConfigCandidates(candidateMetadata, candidates) {
  let sortedAndMerged = [];
  
  candidateMetadata.forEach((candMeta) => {
    const { homepageUrl, imageUrl } = candMeta;
    const candidate = candidates.find(c => c.id === candMeta.candidateId);

    let cp;
    if (candidate) {
      cp = Object.assign({}, candidate);
      if (homepageUrl) {
        cp["website"] = homepageUrl;
      }
      if (imageUrl) {
        cp["image"] = imageUrl;
      }
    }

    // cp will still be undefined if no candidate match was found, so we filter it out
    if(cp) {
      sortedAndMerged.push(cp);
    }
  });

  return sortedAndMerged;
}
